import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserGroupIcon, ChartBarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-shopware-blue to-shopware-dark-blue text-white py-20 md:py-32 overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
          <div className="absolute top-[10%] left-[5%] w-24 h-24 rounded-full bg-white opacity-20"></div>
          <div className="absolute top-[20%] left-[35%] w-32 h-32 rounded-full bg-white opacity-10"></div>
          <div className="absolute top-[15%] right-[15%] w-40 h-40 rounded-full bg-white opacity-10"></div>
          <div className="absolute top-[60%] left-[25%] w-36 h-36 rounded-full bg-white opacity-15"></div>
          <div className="absolute top-[70%] right-[10%] w-28 h-28 rounded-full bg-white opacity-20"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-1 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium">
              Entdecke die neue Art von Lab Days
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
              Lab Day <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-shopware-light-blue">Voting</span>
            </h1>
            <p className="text-lg md:text-xl mb-10 text-white/90 max-w-3xl mx-auto">
              Die moderne Plattform für deine Lab Days. Präsentiere Projekte, sammle Feedback und revolutioniere die Zusammenarbeit im Team.
            </p>
            <div className="flex justify-center">
              <Link
                to="/lab/new"
                className="bg-white text-shopware-blue hover:bg-gray-100 font-medium py-3 px-6 rounded-xl inline-flex items-center justify-center"
              >
                Neuen Lab Day erstellen
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h6 className="text-shopware-blue font-medium mb-3 uppercase tracking-wider">Funktionen</h6>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Alles was du für erfolgreiche Lab Days brauchst
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Unsere Plattform bietet alle Werkzeuge für die Organisation, Durchführung und Auswertung von Lab Days.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-shopware-light-blue rounded-xl flex items-center justify-center mb-6">
                <CalendarIcon className="w-7 h-7 text-shopware-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Einfache Organisation</h3>
              <p className="text-gray-600 mb-4">
                Erstelle und verwalte Lab Days mit wenigen Klicks. Behalte den Überblick über anstehende Events.
              </p>
              <div className="pt-4 border-t border-gray-100">
                <span className="text-sm font-medium text-shopware-blue flex items-center">
                  Mehr erfahren 
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </span>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-shopware-light-blue rounded-xl flex items-center justify-center mb-6">
                <UserGroupIcon className="w-7 h-7 text-shopware-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Kollaboratives Voting</h3>
              <p className="text-gray-600 mb-4">
                Demokratische Entscheidungsfindung durch einfaches Abstimmen und transparente Ergebnisse.
              </p>
              <div className="pt-4 border-t border-gray-100">
                <span className="text-sm font-medium text-shopware-blue flex items-center">
                  Mehr erfahren
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </span>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-shopware-light-blue rounded-xl flex items-center justify-center mb-6">
                <ChartBarIcon className="w-7 h-7 text-shopware-blue" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Echtzeit-Statistiken</h3>
              <p className="text-gray-600 mb-4">
                Verfolge Abstimmungsergebnisse in Echtzeit und gewinne wertvolle Einblicke für zukünftige Events.
              </p>
              <div className="pt-4 border-t border-gray-100">
                <span className="text-sm font-medium text-shopware-blue flex items-center">
                  Mehr erfahren
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h6 className="text-shopware-blue font-medium mb-3 uppercase tracking-wider">Starte jetzt</h6>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900">
              Bereit für deinen nächsten Lab Day?
            </h2>
            <p className="text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
              Verbessere deine Team-Events mit unserer intuitiven Plattform. Registriere dich kostenlos und erlebe, wie einfach die Organisation und Durchführung von Lab Days sein kann.
            </p>
            <Link
              to="/lab/new"
              className="bg-shopware-blue hover:bg-shopware-dark-blue text-white font-medium py-3 px-8 rounded-xl inline-flex items-center justify-center"
            >
              <span>Jetzt starten</span>
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Animation */}
      <div className="h-2 bg-gradient-to-r from-shopware-light-blue via-shopware-blue to-shopware-dark-blue"></div>
    </div>
  );
};

export default Home; 